import { QConfigTimeLimitType } from '@exam-management/graphql';
import { type GetExamsByIdsQuery } from '@exam-room/graphql';
import { type GetQSetByTaskIdQueryResponse } from '@exam-room/graphql/preview';

function assertIsNonEmptyString(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.trim().length > 0 ? value : fallback;
}

function toSafeNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function convertQSetToMockExamData(
  source?: GetQSetByTaskIdQueryResponse,
): GetExamsByIdsQuery {
  const qSet = source?.excGetQSetByTaskId?.qSet ?? null;
  const passingCriteriaByStructures = qSet?.metadata?.passingCriteria?.byStructure ?? [];
  const structures = qSet?.qConfig?.structures ?? [];
  const qConfigStructureParts = qSet?.metadata?.qConfigStructureParts ?? [];
  const timeLimit = qSet?.qConfig?.metadata?.timeLimit ?? null;

  const examId = assertIsNonEmptyString(qSet?.id, 'preview-exam');

  let timeLimitValues = Array.isArray(timeLimit?.values) ? timeLimit?.values : [];

  if (timeLimit?.type === QConfigTimeLimitType.All) {
    const qConfigStructurePartIds = qConfigStructureParts?.map((structure) => structure.id) ?? [];

    timeLimitValues = timeLimitValues.map((v) => ({
      ...v,
      qConfigStructureIds: qConfigStructurePartIds,
    }));
  }

  const filteredTimeLimitValues = timeLimitValues.filter((g: unknown) =>
    Array.isArray((g as { qConfigStructureIds?: string[] | null })?.qConfigStructureIds),
  );

  if (filteredTimeLimitValues.length > 0) {
    const erGetExamsByIds = filteredTimeLimitValues.map((group: unknown, index: number) => {
      const groupObj = (group ?? {}) as {
        order?: number;
        value?: number;
        qConfigStructureIds?: string[];
      };

      const groupIds = Array.isArray(groupObj.qConfigStructureIds)
        ? groupObj.qConfigStructureIds
        : null;
      const parts = Array.isArray(groupIds)
        ? (structures ?? [])
            .filter((s) => groupIds.includes(s.id))
            .map((s) => {
              const passingCriteriaByStructure = passingCriteriaByStructures.find(
                (p) => p.qConfigStructureId === s.id,
              );
              return {
                id: assertIsNonEmptyString(s.id, ''),
                name: assertIsNonEmptyString(s.name, ''),
                order: toSafeNumber(s.order),
                totalQuestions: toSafeNumber(s.totalQuestions),
                passingCriteria: passingCriteriaByStructure?.value
                  ? passingCriteriaByStructure
                  : null,
              };
            })
        : [];

      const totalQuestions = parts.reduce((sum, part) => sum + part.totalQuestions, 0);

      return {
        id: `${examId}-${toSafeNumber(groupObj.order, index)}`,
        title: qSet?.qConfig?.examCourse?.name ?? 'Exam Room Preview',
        totalQuestions: toSafeNumber(totalQuestions),
        metadata: {
          timeLimit: toSafeNumber(groupObj.value),
          qConfigStructureParts: parts,
        },
      };
    });

    return { erGetExamsByIds };
  }

  // or get all
  // const parts = (structures ?? []).map((s) => ({
  //   id: assertIsNonEmptyString(s.id, ''),
  //   name: assertIsNonEmptyString(s.name, ''),
  //   order: toSafeNumber(s.order),
  //   totalQuestions: toSafeNumber(s.totalQuestions),
  //   passingCriteria: null,
  // }));
  const totalQuestions = toSafeNumber(
    qConfigStructureParts.reduce((sum, part) => sum + part.totalQuestions, 0),
  );
  return {
    erGetExamsByIds: [
      {
        id: examId,
        title: qSet?.qConfig?.examCourse?.name ?? 'Exam Room Preview',
        totalQuestions,
        metadata: {
          timeLimit: toSafeNumber(timeLimit?.total),
          qConfigStructureParts,
        },
      },
    ],
  };
}

export default convertQSetToMockExamData;
